import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { CREATE_SIGNUP_LINK, REVOKE_SIGNUP_LINK } from '../../../../graphql/mutations';
import { MY_SIGNUP_LINKS } from '../../../../graphql/queries';
import Button from '../../../../components/Button/Button';
import Notification from '../../../../components/Notification/Notification';
import StatusBadge from '../../../../components/StatusBadge/StatusBadge';
import Table from '../../../../components/Table/Table';
import { ConfirmationModal } from '../../../../components/shared';
import './SignupLinks.css';

const SignupLinks = ({ mates = [] }) => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [revokeConfirmModal, setRevokeConfirmModal] = useState({ isOpen: false, linkId: null });

  const { data, loading, refetch } = useQuery(MY_SIGNUP_LINKS, {
    onError: (error) => {
      setError('Fejl ved hentning af signup links: ' + error.message);
    }
  });

  const [createSignupLink] = useMutation(CREATE_SIGNUP_LINK, {
    onCompleted: () => {
      setSuccess('Nyt signup link oprettet!');
      setError('');
      refetch();
    },
    onError: (error) => {
      setError('Fejl ved oprettelse af signup link: ' + error.message);
    }
  });

  const [revokeSignupLink] = useMutation(REVOKE_SIGNUP_LINK, {
    onCompleted: () => {
      setSuccess('Signup link tilbagekaldt!');
      setError('');
      refetch();
    },
    onError: (error) => {
      setError('Fejl ved tilbagekaldelse af signup link: ' + error.message);
    }
  });

  // Memoized callbacks for better performance
  const handleCreateLink = useCallback(() => {
    setError('');
    setSuccess('');
    createSignupLink();
  }, [createSignupLink]);

  const handleRevokeLink = useCallback((id) => {
    setRevokeConfirmModal({ isOpen: true, linkId: id });
  }, []);

  const confirmRevokeLink = useCallback(() => {
    if (revokeConfirmModal.linkId) {
      setError('');
      setSuccess('');
      revokeSignupLink({ variables: { id: revokeConfirmModal.linkId } });
    }
    setRevokeConfirmModal({ isOpen: false, linkId: null });
  }, [revokeConfirmModal.linkId, revokeSignupLink]);

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess('Link kopieret til udklipsholder!');
    }).catch(() => {
      setError('Kunne ikke kopiere link');
    });
  }, []);

  // Memoized utility functions
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleString('da-DK');
  }, []);

  const getStatusColor = useCallback((link) => {
    if (link.isUsed) return '#ff6b6b';
    if (!link.isValid) return '#ffa726';
    return '#4caf50';
  }, []);

  const getStatusText = useCallback((link) => {
    if (link.isUsed) return 'Brugt';
    if (!link.isValid) return 'Udløbet';
    return 'Aktiv';
  }, []);

  const getLinkUrl = useCallback((token) => `${window.location.origin}/signup/${token}`, []);

  // Memoized values for better performance
  const links = useMemo(() => data?.mySignupLinks || [], [data?.mySignupLinks]);

  // Define table columns
  const columns = useMemo(() => [
    {
      key: 'status',
      title: 'Status',
      minWidth: '120px',
      render: (link) => (
        <StatusBadge color={getStatusColor(link)}>
          {getStatusText(link)}
        </StatusBadge>
      )
    },
    {
      key: 'token',
      title: 'Token',
      minWidth: '150px',
      render: (link) => (
        <div 
          style={{ 
            fontFamily: 'monospace', 
            fontSize: '12px',
            color: '#374151',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }}
          title={link.token}
        >
          {link.token}
        </div>
      )
    },
    {
      key: 'usedBy',
      title: 'Brugt af',
      minWidth: '120px',
      render: (link) => {
        const mateName = mates.find(m => m.id === link.usedBy)?.name || link.usedBy;
        return (
          <div 
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%'
            }}
            title={mateName}
          >
            {mateName}
          </div>
        );
      }
    },
    {
      key: 'createdAt',
      title: 'Oprettet',
      minWidth: '140px',
      render: (link) => (
        <span className="date-text">{formatDate(link.createdAt)}</span>
      )
    },
    {
      key: 'expiresAt',
      title: 'Udløber',
      minWidth: '140px',
      render: (link) => (
        <span className="date-text">{formatDate(link.expiresAt)}</span>
      )
    }
  ], [formatDate, getStatusColor, getStatusText, mates]);

  // Define table actions
  const tableActions = useCallback((link) => {
    const linkUrl = getLinkUrl(link.token);
    
    return (
      <>
        {!link.isUsed && (
          <>
            <Button
              title="Kopier link"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(linkUrl);
              }}
              variant="outlined"
              color="primary"
              size="small"
            />
        
            <Button
              title="Tilbagekald"
              onClick={(e) => {
                e.stopPropagation();
                handleRevokeLink(link.id);
              }}
              variant="outlined"
              color="danger"
              size="small"
            />
          </>
        )}
      </>
    );
  }, [copyToClipboard, handleRevokeLink, getLinkUrl]);

  const renderMobileCard = useCallback((link) => {
    const mateName = mates.find((m) => m.id === link.usedBy)?.name || link.usedBy;
    const linkUrl = getLinkUrl(link.token);

    return (
      <div className="signup-link-mobile-card">
        <div className="signup-link-mobile-top">
          <StatusBadge color={getStatusColor(link)}>
            {getStatusText(link)}
          </StatusBadge>
          <span className="signup-link-mobile-date">{formatDate(link.createdAt)}</span>
        </div>

        <div className="signup-link-mobile-row">
          <span className="signup-link-mobile-label">Token</span>
          <span className="signup-link-mobile-value signup-link-mobile-token" title={link.token}>
            {link.token}
          </span>
        </div>

        <div className="signup-link-mobile-row">
          <span className="signup-link-mobile-label">Brugt af</span>
          <span className="signup-link-mobile-value">{mateName || 'Ikke brugt'}</span>
        </div>

        <div className="signup-link-mobile-row">
          <span className="signup-link-mobile-label">Udløber</span>
          <span className="signup-link-mobile-value">{formatDate(link.expiresAt)}</span>
        </div>

        {!link.isUsed && (
          <div className="signup-link-mobile-actions">
            <Button
              title="Kopier link"
              onClick={() => copyToClipboard(linkUrl)}
              variant="outlined"
              color="primary"
              size="small"
            />
            <Button
              title="Tilbagekald"
              onClick={() => handleRevokeLink(link.id)}
              variant="outlined"
              color="danger"
              size="small"
            />
          </div>
        )}
      </div>
    );
  }, [mates, getLinkUrl, getStatusColor, getStatusText, formatDate, copyToClipboard, handleRevokeLink]);

  // Define table filters
  const tableFilters = useMemo(() => (
    <select 
      className="filter-dropdown"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
    >
      <option value="all">Alle</option>
      <option value="active">Aktive</option>
      <option value="used">Brugte</option>
      <option value="expired">Udløbne</option>
    </select>
  ), [statusFilter]);

  // Memoized filtered and sorted links
  const filteredAndSortedLinks = useMemo(() => {
    let filteredLinks = links;
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filteredLinks = links.filter(link => {
        switch (statusFilter) {
          case 'active':
            return !link.isUsed && link.isValid;
          case 'used':
            return link.isUsed;
          case 'expired':
            return !link.isValid;
          default:
            return true;
        }
      });
    }
    
    // Sort by creation date (newest first)
    return [...filteredLinks].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [links, statusFilter]);

  // Generate dynamic title with count
  const tableTitle = useMemo(() => {
    const count = filteredAndSortedLinks.length;
    const totalCount = links.length;
    if (statusFilter === 'all') {
      return `Antal: ${totalCount}`;
    } else if (statusFilter === 'active') {
      return `Antal: ${count} af ${totalCount}`;
    } else if (statusFilter === 'used') {
      return `Antal: ${count} af ${totalCount}`;
    } else if (statusFilter === 'expired') {
      return `Antal: ${count} af ${totalCount}`;
    }
  }, [filteredAndSortedLinks.length, links.length, statusFilter]);

  return (
    <div className="signup-links">
      <div className="signup-links-header">
        <h3>Signup Links</h3>
        <Button
          title="Opret nyt link"
          onClick={handleCreateLink}
          variant="contained"
          color="primary"
          disabled={loading}
        />
      </div>

      {error && (
        <Notification type="error"
          open={true}
          message={error}
          onClose={() => setError('')}
        />
      )}

      {success && (
        <Notification type="success"
          open={true}
          message={success}
          onClose={() => setSuccess('')}
        />
      )}

      <Table
        data={filteredAndSortedLinks}
        columns={columns}
        loading={loading}
        title={tableTitle}
        emptyMessage="Ingen signup links fundet"
        emptyDescription="Der er ingen signup links oprettet endnu."
        filters={tableFilters}
        actions={tableActions}
        defaultSort={{ key: 'createdAt', direction: 'desc' }}
        searchFields={['token', 'usedBy']}
        responsiveMode="cards"
        mobileCardRenderer={renderMobileCard}
      />

      {/* Revoke Confirmation Modal */}
      <ConfirmationModal
        isOpen={revokeConfirmModal.isOpen}
        onClose={() => setRevokeConfirmModal({ isOpen: false, linkId: null })}
        onConfirm={confirmRevokeLink}
        title="Tilbagekald signup link"
        message="Er du sikker på at du vil tilbagekalde dette signup link?"
        confirmText="Tilbagekald"
        cancelText="Annuller"
        confirmButtonType="danger"
      />
    </div>
  );
};

export default SignupLinks;
